import React from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { ShieldCheck, Lock, Sparkles, UserCheck, KeyRound } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";

const OVERLAY_CONFIG = {
  login: {
    ariaLabel: "Signing into ShreeHomes",
    badge: "Encrypted Auth Protocol",
    step1: {
      title: (
        <>
          Signing you in<span className="text-[#10b981]">...</span>
        </>
      ),
      desc: "Authenticating your security credentials...",
      icon: KeyRound,
    },
    step2: {
      title: (
        <>
          Welcome <span className="text-[#10b981]">back!</span>
        </>
      ),
      desc: "Loading your dashboard & preferences...",
      icon: ShieldCheck,
    },
  },
  register: {
    ariaLabel: "Creating your ShreeHomes account",
    badge: "Verified Registration Protocol",
    step1: {
      title: (
        <>
          Creating your <span className="text-[#10b981]">account...</span>
        </>
      ),
      desc: "Registering profile & security credentials...",
      icon: UserCheck,
    },
    step2: {
      title: (
        <>
          Account <span className="text-[#10b981]">created!</span>
        </>
      ),
      desc: "Setting up your workspace & custom portal...",
      icon: Sparkles,
    },
  },
  logout: {
    ariaLabel: "Signing out of ShreeHomes",
    badge: "Protected Session Protocol",
    step1: {
      title: (
        <>
          Signing you out<span className="text-[#10b981]">...</span>
        </>
      ),
      desc: "Invalidating active session credentials...",
      icon: Lock,
    },
    step2: {
      title: (
        <>
          Securing your <span className="text-[#10b981]">session...</span>
        </>
      ),
      desc: "Clearing local cache & user state...",
      icon: ShieldCheck,
    },
  },
};

/**
 * AuthOverlay Component
 *
 * Full-screen luxury backdrop overlay for Sign In, Registration, and Logout.
 * Features backdrop blur, ambient radial glows, orbiting emblem rings,
 * dynamic step text transitions, and Framer Motion GPU-accelerated animations.
 */
export default function AuthOverlay({ active, type = "login", step = 1 }) {
  const shouldReduceMotion = useReducedMotion();
  const config = OVERLAY_CONFIG[type] || OVERLAY_CONFIG.login;
  const currentStepConfig = step === 1 ? config.step1 : config.step2;
  const StepIcon = currentStepConfig.icon;

  return (
    <AnimatePresence>
      {active && (
        <motion.div
          key={`auth-overlay-${type}`}
          role="dialog"
          aria-modal="true"
          aria-label={config.ariaLabel}
          aria-live="polite"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: shouldReduceMotion ? 0.1 : 0.3, ease: [0.16, 1, 0.3, 1] }}
          className="fixed inset-0 z-[99999] flex flex-col items-center justify-center bg-[#061021]/85 backdrop-blur-xl text-white select-none pointer-events-auto overflow-hidden px-6 text-center"
        >
          {/* Ambient Radial Color Glows */}
          <div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-gradient-to-br from-emerald-500/15 via-amber-500/10 to-transparent blur-3xl pointer-events-none"
            aria-hidden="true"
          />

          <motion.div
            initial={{ opacity: 0, scale: shouldReduceMotion ? 1 : 0.94, y: shouldReduceMotion ? 0 : 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: shouldReduceMotion ? 1 : 1.03, y: shouldReduceMotion ? 0 : -8 }}
            transition={{ duration: shouldReduceMotion ? 0.15 : 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="relative z-10 flex flex-col items-center max-w-sm mx-auto"
          >
            {/* Animated Logo Emblem Outer Ring */}
            <div className="relative mb-8 flex items-center justify-center">
              {/* Outer Orbiting Ring */}
              <motion.div
                animate={shouldReduceMotion ? {} : { rotate: 360 }}
                transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
                className="absolute -inset-4 rounded-full border border-dashed border-emerald-500/30"
                aria-hidden="true"
              />

              {/* Pulsing Emerald Halo */}
              <motion.div
                animate={shouldReduceMotion ? {} : { scale: [1, 1.15, 1], opacity: [0.3, 0.6, 0.3] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                className="absolute -inset-2 rounded-full bg-emerald-500/20 blur-md"
                aria-hidden="true"
              />

              {/* Brand Shell Badge */}
              <div className="relative w-20 h-20 rounded-3xl bg-slate-900/90 border border-white/15 p-4 shadow-2xl backdrop-blur-md flex items-center justify-center">
                <img
                  src="/brand/real-place-mark.svg"
                  alt="ShreeHomes"
                  className="w-12 h-12 object-contain filter drop-shadow-[0_2px_10px_rgba(16,185,129,0.3)]"
                />
              </div>

              {/* Rotating Accent Dot */}
              {!shouldReduceMotion && (
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1.8, repeat: Infinity, ease: "linear" }}
                  className="absolute inset-0 rounded-full"
                  aria-hidden="true"
                >
                  <span className="absolute -top-1 left-1/2 -translate-x-1/2 w-2.5 h-2.5 rounded-full bg-amber-400 shadow-[0_0_8px_#f59e0b]" />
                </motion.div>
              )}
            </div>

            {/* Brand Title */}
            <div className="mb-4">
              <BrandLogo inverse compact size="sm" />
            </div>

            {/* Dynamic Step Headings with Animated Text Swap */}
            <div className="min-h-[72px] flex flex-col items-center justify-center">
              <AnimatePresence mode="wait">
                <motion.div
                  key={`step-${type}-${step}`}
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.2 }}
                  className="flex flex-col items-center"
                >
                  <h2 className="font-display text-2xl font-black tracking-tight text-white">
                    {currentStepConfig.title}
                  </h2>
                  <p className="text-xs font-medium text-slate-400 mt-1.5 flex items-center gap-1.5">
                    <StepIcon className="w-3.5 h-3.5 text-emerald-400" />
                    {currentStepConfig.desc}
                  </p>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Security Pill Badge */}
            <div className="mt-6 inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/25 text-[11px] font-bold text-emerald-400 shadow-inner">
              <Sparkles className="w-3 h-3 text-amber-300 animate-pulse" />
              <span>{config.badge}</span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
